// Запросы окна «Назначить ведущего» (038: contracts/api.md 038; research Р4 – Р6; LEAD-02, LEAD-03).
// Ведущий и что останется у него после смены — minutes.lead.get: окно перечитывает его при каждом
// открытии (staleTime 0). Поиск — minutes.lead.lookup: от двух знаков, с задержкой 250 мс на каждое
// нажатие и прежним ответом на время нового запроса (keepPreviousData), как поиск кандидатов 014.
// Сбой поиска — причина, код, номер операции и «Повторить» (useQueryFailure). После назначения,
// «Отменить» и создания перечитывается всё, где виден ведущий: «Документы», сводка и окно доступа.
import { LOOKUP_QUERY_MAX, LOOKUP_QUERY_MIN } from '@kp/contracts';
import type { LeadLookupPerson, LeadLookupUser } from '@kp/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiKey, apiQuery } from '../../api/client.ts';
import type { ErrorView } from '../../app/errors.ts';
import { useQueryFailure } from '../load-error.tsx';

const SEARCH_DELAY_MS = 250;

/** Ключ кэша ведущего протокола (minutes.lead.get). */
export function leadStateKey(documentId: string): readonly unknown[] {
  return apiKey('minutes.lead.get', { params: { id: documentId } });
}

/** Ведущий протокола и последствие его смены; пока окно открыто, при открытии — свежий. */
export function useLeadState(documentId: string, enabled: boolean) {
  return useQuery({
    ...apiQuery('minutes.lead.get', { params: { id: documentId } }),
    enabled,
    staleTime: 0,
  });
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export interface LeadLookup {
  readonly users: readonly LeadLookupUser[];
  readonly people: readonly LeadLookupPerson[];
  /** Введённое без пробелов по краям. */
  readonly typed: string;
  /** Введено от двух знаков. */
  readonly ready: boolean;
  /** Ответа на введённое ещё нет. */
  readonly loading: boolean;
  /** Ответ на введённое пришёл и пуст в обоих разделах. */
  readonly nobody: boolean;
  readonly failure: ErrorView | null;
  /** Ошибка ответа, пока сбой на виду: по коду окно решает, закрыться ли (REC-002, MIN-001). */
  readonly error: unknown;
  readonly retrying: boolean;
  readonly retry: () => void;
}

/** Пользователи и люди справочника по ФИО или почте (FR-004, FR-005). */
export function useLeadLookup(documentId: string, text: string, enabled: boolean): LeadLookup {
  const typed = text.trim().slice(0, LOOKUP_QUERY_MAX);
  const q = useDebounced(typed, SEARCH_DELAY_MS);
  const ready = typed.length >= LOOKUP_QUERY_MIN;
  const lookup = useQuery({
    ...apiQuery('minutes.lead.lookup', { params: { id: documentId }, query: { q } }),
    enabled: enabled && q.length >= LOOKUP_QUERY_MIN,
    placeholderData: keepPreviousData,
  });
  const { failure, retrying, retry } = useQueryFailure(lookup, { show: ready });
  const data = ready && !failure ? lookup.data : undefined;
  const users = data?.users ?? [];
  const people = data?.people ?? [];
  const answered =
    q === typed && lookup.isSuccess && !lookup.isFetching && !lookup.isPlaceholderData;
  return {
    users,
    people,
    typed,
    ready,
    loading: ready && !failure && !answered,
    nobody: ready && !failure && answered && users.length === 0 && people.length === 0,
    failure,
    error: failure && lookup.isError ? lookup.error : null,
    retrying,
    retry,
  };
}

/**
 * Ведущий сменился: «Документы», сводка доступа, окно «Доступ» и состояние ведущего; 0.32.1 —
 * права сеанса: прежний владелец теряет действия владельца без перехода в другой раздел.
 */
export function refreshLead(queryClient: QueryClient, documentId: string, created = false): void {
  for (const name of [
    'documents.list',
    'manager.list',
    'manager.activity',
    'minutes.access.summary',
    'minutes.lead.get',
    'auth.me',
  ] as const) {
    void queryClient.invalidateQueries({ queryKey: ['api', name] });
  }
  void queryClient.invalidateQueries({
    queryKey: apiKey('minutes.access.get', { params: { id: documentId } }),
  });
  if (created) void queryClient.invalidateQueries({ queryKey: ['api', 'users.list'] });
}
